import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import Department from "models/department";
import DepartmentMapping from "models/department-mapping";
import EmployeeDepartmentPermission from "models/employee-department-permission";

export interface AdminDepartmentsSliceState {
    departments: Department[];
    departmentPermissions: EmployeeDepartmentPermission[];
    departmentMappings: DepartmentMapping[];
    isGlobalAdmin: boolean;
}

export const initialState: AdminDepartmentsSliceState = {
    departments: [],
    departmentPermissions: [],
    departmentMappings: [],
    isGlobalAdmin: false,
};

const AdminDepartmentsSlice = createSlice({
    name: "admin-departments-slice",
    initialState,
    reducers: {
        setDepartments(state, { payload }: PayloadAction<Department[]>) {
            state.departments = payload;
        },
        setIsGlobalAdmin(state, { payload }: PayloadAction<boolean>) {
            state.isGlobalAdmin = payload;
        },
        addDepartment(state, { payload }: PayloadAction<Department>) {
            state.departments = [payload, ...state.departments];
        },
        setDepartment(state, { payload }: PayloadAction<Department>) {
            const index = state.departments.findIndex(d => d.id === payload.id);
            if (index !== -1) {
                state.departments[index] = payload;
            }
        },
        removeDepartment(state, { payload: departmentId }: PayloadAction<string>) {
            state.departments = state.departments.filter(d => d.id !== departmentId);
        },
        setDepartmentPermissions(state, { payload }: PayloadAction<EmployeeDepartmentPermission[]>) {
            state.departmentPermissions = payload;
        },
        upsertDepartmentPermission(state, { payload }: PayloadAction<EmployeeDepartmentPermission>) {
            const index = state.departmentPermissions.findIndex(p => p.employeeId === payload.employeeId);
            if (index !== -1) {
                state.departmentPermissions[index] = payload;
            } else {
                state.departmentPermissions = [payload, ...state.departmentPermissions];
            }
        },
        removeDepartmentPermission(state, { payload: employeeId }: PayloadAction<string>) {
            state.departmentPermissions = state.departmentPermissions.filter(p => p.employeeId !== employeeId);
        },
        setDepartmentMappings(state, { payload }: PayloadAction<DepartmentMapping[]>) {
            state.departmentMappings = payload;
        },
        addDepartmentMapping(state, { payload }: PayloadAction<DepartmentMapping>) {
            state.departmentMappings = [payload, ...state.departmentMappings];
        },
        removeDepartmentMapping(state, { payload: mappingId }: PayloadAction<string>) {
            state.departmentMappings = state.departmentMappings.filter(m => m.id !== mappingId);
        },
    },
});

export const {
    setDepartments,
    setIsGlobalAdmin,
    addDepartment,
    setDepartment,
    removeDepartment,
    setDepartmentPermissions,
    upsertDepartmentPermission,
    removeDepartmentPermission,
    setDepartmentMappings,
    addDepartmentMapping,
    removeDepartmentMapping,
} = AdminDepartmentsSlice.actions;

export default AdminDepartmentsSlice.reducer;
